/**
 * Homework structured surfaces · shared, reusable working surfaces (Story / AC list /
 * MoSCoW / bug-report / free text). Same building blocks the Atelier reuses, so a learner
 * does the homework in-place instead of switching apps. slot_homework_rework Ф2.
 *
 * Each surface owns a JSON-serialisable value (persisted in HomeworkProgress.submissionData)
 * and reports `filled` (gates «Сдать» alongside the self-check). A human-readable form is
 * produced by `serializeSurface` and mirrored into `submission` for the portfolio.
 */

export type HomeworkSurfaceKind =
  | 'freeText'
  | 'storyForm'
  | 'acList'
  | 'moscowBoard'
  | 'bugReport'
  | 'testCase'
  | 'table'
  | 'code';

/** Языки код-редактора (совпадают с токенайзером codeFormat.highlight). */
export type CodeLang = 'js' | 'sql' | 'python' | 'bash';

export interface HomeworkSurfaceConfig {
  placeholder?: string; // freeText
  minChars?: number; // freeText (default 10)
  minAc?: number; // storyForm acceptance-criteria minimum (default 2)
  minItems?: number; // acList minimum filled rows (default 3)
  itemLabel?: string; // acList row noun ("критерий" / "дыра")
  requirement?: string; // acList/holes — text shown above (the thing being reviewed)
  pool?: string[]; // moscowBoard — items to sort into columns
  minAssigned?: number; // moscowBoard — minimum assigned (default = pool length)
  columns?: Array<{ key: string; label: string }>; // moscowBoard — custom columns (default MoSCoW)
  variant?: 'base' | 'full'; // bugReport — base (ядро) / full (+приоритет/частота/влияние · 11 полей S07)
  tableColumns?: string[]; // table — заголовки колонок (2D-сетка: RTM, decision, матрица…)
  minRows?: number; // table — минимум заполненных строк (default 2)
  codeLang?: CodeLang; // code — язык подсветки/бьютификации (default 'js')
  starter?: string; // code — стартовый шаблон/скелет в редакторе
}

export interface HomeworkSurfaceSpec {
  kind: HomeworkSurfaceKind;
  config?: HomeworkSurfaceConfig;
}

export interface FreeTextValue { text: string }
export interface StoryFormValue { role: string; action: string; value: string; ac: string[] }
export interface AcListValue { items: string[] }
export type MoscowKey = 'must' | 'should' | 'could' | 'wont';
export type MoscowValue = Record<string, string[]>;
export const DEFAULT_MOSCOW_COLS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'must', label: 'Must' },
  { key: 'should', label: 'Should' },
  { key: 'could', label: 'Could' },
  { key: "wont", label: "Won't" },
];
export interface BugReportValue {
  title: string; description: string; severity: string; env: string;
  steps: string; expected: string; actual: string; attachments: string;
  // full-вариант (S07 · 11 полей):
  priority?: string; frequency?: string; impact?: string;
}
export interface TestCaseValue {
  tcId: string; title: string; precondition: string; steps: string;
  expected: string; postcondition: string;
}
/** table — строки по числу колонок config.tableColumns. */
export interface TableValue { rows: string[][] }
/** code — редактор кода (одно поле, язык из config.codeLang). */
export interface CodeValue { code: string }

export type SurfaceValue =
  | FreeTextValue | StoryFormValue | AcListValue | MoscowValue | BugReportValue;

export interface SurfaceProps {
  spec: HomeworkSurfaceSpec;
  value: Record<string, unknown> | undefined;
  disabled?: boolean;
  onChange: (value: Record<string, unknown>, filled: boolean) => void;
}

/** Default empty value for a surface kind (used on first open). */
export function emptySurfaceValue(spec: HomeworkSurfaceSpec): Record<string, unknown> {
  switch (spec.kind) {
    case 'storyForm': return { role: '', action: '', value: '', ac: ['', ''] };
    case 'acList': return { items: ['', '', ''] };
    case 'moscowBoard': return {};
    case 'bugReport': {
      const base = { title: '', description: '', severity: '', env: '', steps: '', expected: '', actual: '', attachments: '' };
      return spec.config?.variant === 'full' ? { ...base, priority: '', frequency: '', impact: '' } : base;
    }
    case 'testCase': return { tcId: '', title: '', precondition: '', steps: '', expected: '', postcondition: '' };
    case 'table': {
      const cols = spec.config?.tableColumns ?? ['Колонка 1', 'Колонка 2'];
      const emptyRow = () => cols.map(() => '');
      const n = Math.max(2, spec.config?.minRows ?? 2);
      return { rows: Array.from({ length: n }, emptyRow) };
    }
    case 'code': return { code: spec.config?.starter ?? '' };
    case 'freeText':
    default: return { text: '' };
  }
}

const nz = (s: unknown) => typeof s === 'string' && s.trim().length > 0;

/** Whether the surface is filled enough to allow submission. */
export function surfaceFilled(spec: HomeworkSurfaceSpec, v: Record<string, unknown>): boolean {
  const c = spec.config ?? {};
  switch (spec.kind) {
    case 'storyForm': {
      const ac = Array.isArray(v.ac) ? (v.ac as string[]).filter(nz) : [];
      return nz(v.role) && nz(v.action) && nz(v.value) && ac.length >= (c.minAc ?? 2);
    }
    case 'acList': {
      const items = Array.isArray(v.items) ? (v.items as string[]).filter(nz) : [];
      return items.length >= (c.minItems ?? 3);
    }
    case 'moscowBoard': {
      const pool = c.pool ?? [];
      const cols = c.columns ?? DEFAULT_MOSCOW_COLS;
      const assigned = cols.reduce((n, col) => n + ((v[col.key] as string[] | undefined)?.length ?? 0), 0);
      return assigned >= (c.minAssigned ?? pool.length) && pool.length > 0;
    }
    case 'bugReport':
      return nz(v.title) && nz(v.severity) && nz(v.steps) && nz(v.expected) && nz(v.actual);
    case 'testCase':
      return nz(v.title) && nz(v.precondition) && nz(v.steps) && nz(v.expected);
    case 'table': {
      const rows = Array.isArray(v.rows) ? (v.rows as string[][]) : [];
      const full = rows.filter((r) => Array.isArray(r) && r.every((cell) => nz(cell)));
      return full.length >= Math.max(1, (c.minRows ?? 2));
    }
    case 'code':
      return nz(v.code) && (v.code as string).trim().length >= (c.minChars ?? 20);
    case 'freeText':
    default:
      return nz(v.text) && (v.text as string).trim().length >= (c.minChars ?? 10);
  }
}

/**
 * ── Backbone #2 · детерминированная авто-проверка сдачи (Genie 11 JUL) ──
 * Гоняет структурные проверки по РЕАЛЬНОМУ вводу студента и возвращает конкретный
 * фидбэк («в Story №2 нет ценности», «3 из 6 AC очень короткие», «ожидаемо = фактически»).
 * Чистая функция, без LLM и бэкенда — мгновенно, $0, никогда не ошибается по структуре.
 * НЕ судит смысл (это слой #3 · AI). Advisory: не блокирует сдачу, только помогает.
 */
export type SurfaceCheckStatus = 'ok' | 'warn' | 'fail';
export interface SurfaceCheck {
  status: SurfaceCheckStatus;
  label: string;
  /** конкретная находка по вводу (что именно и где). */
  detail?: string;
}

const arr = (x: unknown): string[] => (Array.isArray(x) ? (x as unknown[]).map(String) : []);
const trimmed = (x: unknown): string => (typeof x === 'string' ? x.trim() : '');
const SEVERITY_WORDS = ['blocker', 'critical', 'major', 'minor', 'trivial', 'блокер', 'критич', 'мажор', 'минор', 'тривиал'];

/** Найти индексы дублей (1-based) в списке непустых строк. */
function dupIndexes(items: string[]): number[] {
  const seen = new Map<string, number>();
  const dups: number[] = [];
  items.forEach((it, i) => {
    const key = it.trim().toLowerCase();
    if (!key) return;
    if (seen.has(key)) dups.push(i + 1);
    else seen.set(key, i + 1);
  });
  return dups;
}

export function checkSurface(spec: HomeworkSurfaceSpec, v: Record<string, unknown>): SurfaceCheck[] {
  const c = spec.config ?? {};
  const out: SurfaceCheck[] = [];

  switch (spec.kind) {
    case 'storyForm': {
      const role = trimmed(v.role), action = trimmed(v.action), value = trimmed(v.value);
      const missing = [!role && 'роль', !action && 'действие', !value && 'ценность'].filter(Boolean);
      out.push(missing.length
        ? { status: 'fail', label: 'Заполнены все три части Story', detail: `не хватает: ${missing.join(', ')}` }
        : { status: 'ok', label: 'Все три части Story на месте (роль · действие · ценность)' });
      if (['пользователь', 'user', 'юзер', 'клиент'].includes(role.toLowerCase())) {
        out.push({ status: 'warn', label: 'Роль конкретная', detail: `«${role}» — дежурная роль; уточни, кто именно (покупатель, продавец, менеджер «Артели»)` });
      }
      const ac = arr(v.ac).filter((s) => s.trim());
      const minAc = c.minAc ?? 2;
      out.push(ac.length >= minAc
        ? { status: 'ok', label: `Критериев приёмки: ${ac.length} (нужно ≥ ${minAc})` }
        : { status: 'fail', label: 'Достаточно критериев приёмки', detail: `${ac.length} из ${minAc}` });
      const shortAc = ac.map((s, i) => (s.trim().length < 12 ? i + 1 : 0)).filter(Boolean);
      if (shortAc.length) out.push({ status: 'warn', label: 'Критерии не слишком короткие', detail: `очень короткие: №${shortAc.join(', №')}` });
      const dup = dupIndexes(ac);
      if (dup.length) out.push({ status: 'warn', label: 'Критерии не дублируются', detail: `повтор: №${dup.join(', №')}` });
      break;
    }
    case 'acList': {
      const items = arr(v.items).filter((s) => s.trim());
      const minItems = c.minItems ?? 3;
      const noun = c.itemLabel ?? 'пункт';
      out.push(items.length >= minItems
        ? { status: 'ok', label: `Заполнено ${noun}ов: ${items.length} (нужно ≥ ${minItems})` }
        : { status: 'fail', label: `Достаточно (${noun})`, detail: `${items.length} из ${minItems}` });
      const shortI = items.map((s, i) => (s.trim().length < 12 ? i + 1 : 0)).filter(Boolean);
      if (shortI.length) out.push({ status: 'warn', label: 'Пункты не слишком короткие', detail: `очень короткие: №${shortI.join(', №')}` });
      const dup = dupIndexes(items);
      if (dup.length) out.push({ status: 'warn', label: 'Пункты не дублируются', detail: `повтор: №${dup.join(', №')}` });
      const firstWords = items.map((s) => s.trim().toLowerCase().split(/\s+/)[0] ?? '').filter(Boolean);
      const wc = new Map<string, number>();
      firstWords.forEach((w) => wc.set(w, (wc.get(w) ?? 0) + 1));
      const same = [...wc.values()].some((n) => n >= 3);
      if (same && items.length >= 3) out.push({ status: 'warn', label: 'Пункты про разные ситуации', detail: 'много пунктов начинаются одинаково — проверь, что покрываешь разное, а не переформулировки' });
      break;
    }
    case 'moscowBoard': {
      const pool = c.pool ?? [];
      const cols = c.columns ?? DEFAULT_MOSCOW_COLS;
      const counts = cols.map((col) => arr(v[col.key]).filter((s) => s.trim()).length);
      const assigned = counts.reduce((a, b) => a + b, 0);
      out.push(assigned >= pool.length && pool.length > 0
        ? { status: 'ok', label: `Все задачи распределены: ${assigned} из ${pool.length}` }
        : { status: 'fail', label: 'Все задачи распределены', detail: `${assigned} из ${pool.length}` });
      if (assigned >= 3) {
        const maxCol = Math.max(...counts);
        // всё (или всё кроме одного) свалено в одну колонку — это не приоритизация.
        if (maxCol >= assigned - 1) {
          const idx = counts.indexOf(maxCol);
          out.push({ status: 'warn', label: 'Распределение неравномерное', detail: `почти всё в «${cols[idx]?.label}» — реальный бэклог редко живёт в одной категории` });
        }
      }
      const isDefault = cols === DEFAULT_MOSCOW_COLS || cols.some((col) => col.key === 'wont');
      const wontCol = cols.find((col) => col.key === 'wont');
      if (isDefault && wontCol && arr(v['wont']).filter((s) => s.trim()).length === 0 && assigned > 0) {
        out.push({ status: 'warn', label: 'Есть явное «не сейчас» (Won’t)', detail: 'колонка «не делаем» пустая — явное «не в этом спринте» тоже решение' });
      }
      break;
    }
    case 'bugReport': {
      const fields: Array<[string, string]> = [
        ['заголовок', trimmed(v.title)], ['severity', trimmed(v.severity)],
        ['шаги', trimmed(v.steps)], ['ожидаемо', trimmed(v.expected)], ['фактически', trimmed(v.actual)],
      ];
      const missing = fields.filter(([, val]) => !val).map(([k]) => k);
      out.push(missing.length
        ? { status: 'fail', label: 'Заполнены обязательные поля', detail: `не хватает: ${missing.join(', ')}` }
        : { status: 'ok', label: 'Все обязательные поля заполнены' });
      const sev = trimmed(v.severity).toLowerCase();
      if (sev && !SEVERITY_WORDS.some((w) => sev.includes(w))) {
        out.push({ status: 'warn', label: 'Severity из шкалы', detail: `«${trimmed(v.severity)}» вне шкалы — обычно Blocker / Critical / Major / Minor / Trivial` });
      }
      const steps = trimmed(v.steps);
      if (steps && !/\d/.test(steps) && !steps.includes('\n')) {
        out.push({ status: 'warn', label: 'Шаги пронумерованы / атомарны', detail: 'шаги одной строкой — разбей по шагам (1., 2., 3.), чтобы баг повторили точь-в-точь' });
      }
      const exp = trimmed(v.expected), act = trimmed(v.actual);
      if (exp && act && exp.toLowerCase() === act.toLowerCase()) {
        out.push({ status: 'fail', label: 'Ожидаемо и фактически различаются', detail: 'они совпадают — а должны противоречить (в этом суть бага)' });
      }
      const title = trimmed(v.title);
      if (title && (title.length < 15 || /^(не работает|сломано|баг|ошибка|глючит)\.?$/i.test(title))) {
        out.push({ status: 'warn', label: 'Заголовок узнаваем', detail: 'слишком общий — формула «что + где + при каком условии»' });
      }
      if (!trimmed(v.description)) {
        out.push({ status: 'warn', label: 'Есть описание/суть', detail: 'заголовок — одной строкой, описание — 1-2 предложения контекста; поле пустое' });
      }
      if (!trimmed(v.attachments)) {
        out.push({ status: 'warn', label: 'Указаны приложения (evidence)', detail: 'что приложить: скриншот шага, лог, запрос — конкретно, не «приложу скриншот»' });
      }
      if (c.variant === 'full') {
        const fullMissing = [!trimmed(v.priority) && 'приоритет', !trimmed(v.frequency) && 'частота', !trimmed(v.impact) && 'влияние на бизнес'].filter(Boolean);
        if (fullMissing.length) out.push({ status: 'warn', label: 'Заполнены поля полного репорта', detail: `не хватает: ${fullMissing.join(', ')}` });
      }
      break;
    }
    case 'testCase': {
      const title = trimmed(v.title), pre = trimmed(v.precondition), steps = trimmed(v.steps), exp = trimmed(v.expected);
      const missing = [!title && 'заголовок', !pre && 'предусловие', !steps && 'шаги', !exp && 'ожидаемый результат'].filter(Boolean);
      out.push(missing.length
        ? { status: 'fail', label: 'Заполнены обязательные поля тест-кейса', detail: `не хватает: ${missing.join(', ')}` }
        : { status: 'ok', label: 'Все обязательные поля тест-кейса на месте' });
      if (steps && !/\d/.test(steps) && !steps.includes('\n')) {
        out.push({ status: 'warn', label: 'Шаги атомарны и пронумерованы', detail: 'шаги одной строкой — разбей по шагам (1., 2., 3.), одно действие на шаг' });
      }
      if (exp && /^(корректно|правильно|ок|работает|успешно)\.?$/i.test(exp)) {
        out.push({ status: 'warn', label: 'Ожидаемый результат проверяемый', detail: 'слишком общий — что конкретно видно на экране / в данных (число, текст, состояние)' });
      }
      if (!trimmed(v.postcondition)) {
        out.push({ status: 'warn', label: 'Есть постусловие', detail: 'что осталось в системе после кейса (данные, сессия) — поле пустое' });
      }
      break;
    }
    case 'table': {
      const cols = c.tableColumns ?? [];
      const rows = Array.isArray(v.rows) ? (v.rows as string[][]) : [];
      const fullRows = rows.filter((r) => Array.isArray(r) && r.some((cell) => trimmed(cell)));
      const minR = c.minRows ?? 2;
      const complete = rows.filter((r) => Array.isArray(r) && r.length === cols.length && r.every((cell) => trimmed(cell)));
      out.push(complete.length >= minR
        ? { status: 'ok', label: `Заполнено строк: ${complete.length} (нужно ≥ ${minR})` }
        : { status: 'fail', label: 'Достаточно полных строк', detail: `${complete.length} из ${minR} (строка полная, когда заполнены все колонки)` });
      // частично заполненные строки (есть данные, но не во всех колонках)
      const partial = fullRows.filter((r) => !r.every((cell) => trimmed(cell))).length;
      if (partial) out.push({ status: 'warn', label: 'Нет полупустых строк', detail: `${partial} строк(и) заполнены не во всех колонках` });
      break;
    }
    case 'code': {
      const code = trimmed(v.code);
      const lang = c.codeLang ?? 'js';
      const min = c.minChars ?? 20;
      out.push(code.length >= min
        ? { status: 'ok', label: `Есть решение (${code.length} символов)` }
        : { status: 'fail', label: 'Есть решение', detail: `${code.length} из ${min} символов — пусто или почти` });
      if (code && lang === 'js') {
        if (/waitfortimeout|settimeout|\bsleep\s*\(/i.test(code)) {
          out.push({ status: 'warn', label: 'Нет фиксированного «сна»', detail: 'жёсткая пауза по таймеру хрупкая — жди условие: await expect(...).toBeVisible()' });
        }
        if (!/\bexpect\s*\(/i.test(code)) {
          out.push({ status: 'warn', label: 'Есть проверка результата', detail: 'не вижу assert (expect(...)) — тест без проверки не поймает баг' });
        }
        if (/xpath=|\/\/\w+\[\d+\]|:nth-child\(\d+\)/i.test(code)) {
          out.push({ status: 'warn', label: 'Локаторы устойчивые', detail: 'похоже на индексный/абсолютный локатор — предпочитай роль, тест-id или текст' });
        }
      }
      if (code && lang === 'sql') {
        if (/select\s+\*/i.test(code)) {
          out.push({ status: 'warn', label: 'Явные колонки вместо *', detail: 'SELECT * хрупок к смене схемы — перечисли нужные поля' });
        }
        if (/\b(update|delete)\b/i.test(code) && !/\bwhere\b/i.test(code)) {
          out.push({ status: 'warn', label: 'UPDATE/DELETE c WHERE', detail: 'запрос меняет данные без WHERE — заденет все строки' });
        }
      }
      if (code && lang === 'bash') {
        if (/(password|token|api[_-]?key|secret)\s*[:=]\s*\S/i.test(code)) {
          out.push({ status: 'warn', label: 'Секрет не в открытую', detail: 'ключ/пароль прямо в команде — вынеси в переменную окружения' });
        }
      }
      break;
    }
    case 'freeText':
    default: {
      const text = trimmed(v.text);
      const min = c.minChars ?? 10;
      out.push(text.length >= min
        ? { status: 'ok', label: `Объём: ${text.length} символов (нужно ≥ ${min})` }
        : { status: 'fail', label: 'Достаточный объём', detail: `${text.length} из ${min} символов` });
      if (/^https?:\/\/\S+$/.test(text)) {
        out.push({ status: 'warn', label: 'Не только ссылка', detail: 'это только ссылка — добавь пару слов, что где смотреть' });
      }
      break;
    }
  }
  return out;
}

/** Human-readable serialisation → mirrored into `submission` for the portfolio. */
export function serializeSurface(spec: HomeworkSurfaceSpec, v: Record<string, unknown>): string {
  switch (spec.kind) {
    case 'storyForm': {
      const ac = (Array.isArray(v.ac) ? (v.ac as string[]) : []).filter(nz);
      return `Как ${v.role}, я хочу ${v.action}, чтобы ${v.value}.\n` +
        ac.map((a, i) => `AC-${i + 1}: ${a}`).join('\n');
    }
    case 'acList': {
      const items = (Array.isArray(v.items) ? (v.items as string[]) : []).filter(nz);
      return items.map((a, i) => `${i + 1}. ${a}`).join('\n');
    }
    case 'moscowBoard':
      return (spec.config?.columns ?? DEFAULT_MOSCOW_COLS)
        .map((col) => `${col.label}: ${((v[col.key] as string[]) ?? []).join(', ') || '—'}`)
        .join('\n');
    case 'bugReport': {
      const line = (label: string, val: unknown) => (trimmed(val) ? `${label}: ${trimmed(val)}\n` : '');
      return `${trimmed(v.title)}\n` +
        line('Описание', v.description) +
        line('Серьёзность', v.severity) + line('Приоритет', v.priority) +
        line('Окружение', v.env) +
        `Шаги: ${trimmed(v.steps)}\nОжидаемо: ${trimmed(v.expected)}\nФактически: ${trimmed(v.actual)}\n` +
        line('Частота', v.frequency) + line('Влияние на бизнес', v.impact) +
        line('Приложения', v.attachments);
    }
    case 'testCase': {
      const p = (label: string, val: unknown) => (trimmed(val) ? `${label}: ${trimmed(val)}\n` : '');
      return p('TC', v.tcId) + `${trimmed(v.title)}\n` + p('Предусловие', v.precondition) +
        `Шаги: ${trimmed(v.steps)}\nОжидаемый результат: ${trimmed(v.expected)}\n` + p('Постусловие', v.postcondition);
    }
    case 'table': {
      const cols = spec.config?.tableColumns ?? [];
      const rows = Array.isArray(v.rows) ? (v.rows as string[][]) : [];
      const header = cols.join(' | ');
      const body = rows
        .filter((r) => Array.isArray(r) && r.some((cell) => nz(cell)))
        .map((r) => r.map((cell) => String(cell ?? '')).join(' | '))
        .join('\n');
      return `${header}\n${body}`;
    }
    case 'code': {
      const lang = spec.config?.codeLang ?? 'js';
      return `\`\`\`${lang}\n${String(v.code ?? '')}\n\`\`\``;
    }
    case 'freeText':
    default:
      return String(v.text ?? '');
  }
}
